import type { MenuItem } from './types';

type HasRoutePermission = (path: string) => boolean;
type HasOperationPermission = (operationId: string) => boolean;

/**
 * Recursively filter MENU_CONFIG by the user's permissions.
 *
 * Vue parity — `RW-ERP-Service/src/components/navigation/AsideMenu.vue`
 * lines 1325-1362 (`getFilteredMenuItems` → `filterMenuItems`).
 *
 * The Vue function's exact decision tree, reproduced here verbatim:
 *
 *   for each item:
 *     1. if item.childMenu exists:
 *          filtered = filterMenuItems(childMenu)
 *          if filtered.length > 0:
 *              return { ...item, childMenu: filtered }   // keep with filtered kids
 *          // else FALL THROUGH — do NOT early-return
 *
 *     2. if hasPermissionForRoute(item.routeLink):
 *          return item                                    // keep as leaf
 *
 *     3. return null                                      // drop
 *
 * Subtle: step 2 is reached BOTH for true leaves (no childMenu) AND for
 * parents whose children all filtered out. A parent that has its own
 * routeLink can therefore survive even when none of its sub-pages are
 * accessible — Vue treats the parent as a leaf in that case.
 *
 * Previous React behaviour `continue`d straight past the leaf check
 * whenever a parent had children, which would drop a parent + own-route
 * item that Vue kept. Mirroring Vue here closes that divergence.
 *
 * Order of remaining items is preserved — iteration walks the source
 * array in insertion order.
 *
 * Extension: items with `operationId` are gated by hasOperationPermission
 * BEFORE the standard route-permission tree runs. If the operation
 * permission is denied the entire branch (item + all descendants) is dropped
 * regardless of route permissions or skipPermissionCheck.
 */
export function filterMenuByPermissions(
  items: ReadonlyArray<MenuItem>,
  hasPermissionForRoute: HasRoutePermission,
  hasOperationPermission: HasOperationPermission = () => false
): MenuItem[] {
  const walk = (list: ReadonlyArray<MenuItem>): MenuItem[] => {
    const kept: MenuItem[] = [];
    for (const item of list) {
      // Operation-level gate — checked first, gates the whole branch.
      if (item.operationId && !hasOperationPermission(item.operationId)) {
        continue;
      }

      // Step 1 — parent recursion.
      if (item.children && item.children.length > 0) {
        const filteredChildren = walk(item.children);
        if (filteredChildren.length > 0) {
          kept.push({ ...item, children: filteredChildren });
          continue;
        }
        // Children all filtered out → fall through to step 2.
        // Matches Vue's lack of early-return inside the `if (childMenu)`
        // block (AsideMenu.vue:1331-1344).
      }
      // Step 2 — leaf / parent-with-own-route check.
      // skipPermissionCheck bypasses hasPermissionForRoute for pages that have
      // no backend UUID yet (see MenuItem.skipPermissionCheck in types.ts).
      if (item.skipPermissionCheck || (item.route && hasPermissionForRoute(item.route))) {
        kept.push(item);
      }
      // Step 3 — implicit drop (no push).
    }
    return kept;
  };
  return walk(items);
}
